'use strict'

/**
 * Fee resolution + assay-fee fan-out + reference-fee-schedule shape
 * validation.
 *
 * Answers: "what does this contract charge, given a unit basis?"
 *
 * Sections (top → bottom):
 *   - resolveFeeAmount + matchPurityBand (perToz / flat / flatPerEvent +
 *     purity-tier bands)
 *   - legacy `standardFees.treatmentFeeUsd` → `otherFees[]` fallback
 *   - assay-fee map fan-out + sanitization + opt-in tracking
 *   - reference-only fee-schedule shape validation
 *
 * Resolve upstream contract 'otherFees' rows to USD amounts. 'kind: perToz'
 * rows may carry 'purityTiers: [{minPct, maxPct, perTozUsd, minimumUsd?}]'
 * under half-open '[minPct, maxPct)'; below the lowest band returns $0
 * (treatment not chargeable below fineness floor). When absent/empty, flat
 * 'perTozUsd' semantics apply.
 */

import {IMPURITY_BAND_STATES} from '../constants'
import {
  hasMeaningfulValue,
  isPlainObject,
  ALLOWED_IMPURITY_BAND_STATES,
  IMPURITY_CHARGE_BASES,
  REFERENCE_FEE_SCHEDULE_ELEMENTS,
  REFINING_COST_BASES,
} from './_shared'

// ============================================================================
// Fee resolution (perToz / flat / flatPerEvent + optional purity-tier bands)
// ============================================================================

/**
 * @param {object} fee - `{ code, kind, flatUsd?, perTozUsd?, minimumUsd?, basis?, conditional?, purityTiers? }`
 * @param {object} ctx - `{ reportedGrossToz, basisToz?, qualifyingPurityPct? }`
 */
export const resolveFeeAmount = (fee, ctx = {}) => {
  if (!fee || typeof fee !== 'object') return 0
  const kind = fee.kind
  if (kind === 'flat') {
    return Number.isFinite(fee.flatUsd) ? fee.flatUsd : 0
  }
  if (kind === 'flatPerEvent') {
    return Number.isFinite(fee.flatUsd) ? fee.flatUsd : 0
  }
  if (kind === 'perToz') {
    const basisToz = Number.isFinite(ctx.basisToz)
      ? ctx.basisToz
      : (Number.isFinite(ctx.reportedGrossToz) ? ctx.reportedGrossToz : 0)
    const tiers = Array.isArray(fee.purityTiers) ? fee.purityTiers : null
    if (tiers && tiers.length > 0) {
      const band = matchPurityBand(tiers, ctx.qualifyingPurityPct)
      if (!band) return 0
      const perTozUsd = Number.isFinite(band.perTozUsd) ? band.perTozUsd : 0
      const raw = perTozUsd * basisToz
      const minimumUsd = Number.isFinite(band.minimumUsd) ? band.minimumUsd : 0
      return Math.max(raw, minimumUsd)
    }
    const perTozUsd = Number.isFinite(fee.perTozUsd) ? fee.perTozUsd : 0
    const raw = perTozUsd * basisToz
    const minimumUsd = Number.isFinite(fee.minimumUsd) ? fee.minimumUsd : 0
    return Math.max(raw, minimumUsd)
  }
  return 0
}

// Half-open '[minPct, maxPct)' lookup. First matching band in declaration order wins; below the lowest 'minPct' returns null. Exported so read-side renderers share the canonical band-matching algorithm.
export const matchPurityBand = (tiers, qualifyingPurityPct) => {
  if (!Array.isArray(tiers) || !Number.isFinite(qualifyingPurityPct)) return null
  for (const band of tiers) {
    if (!band) continue
    const min = Number.isFinite(band.minPct) ? band.minPct : 0
    const max = Number.isFinite(band.maxPct) ? band.maxPct : Infinity
    if (qualifyingPurityPct >= min && qualifyingPurityPct < max) return band
  }
  return null
}

// ============================================================================
// Legacy `standardFees.treatmentFeeUsd` → `otherFees[]` fallback
// ----------------------------------------------------------------------------
// Pre-extension contracts predated the line-items `otherFees[]` array and
// carried a single flat `standardFees.treatmentFeeUsd` instead. The contract
// editor, contract-switch preview, and settlement mockApi all need to read
// `otherFees` with the same fallback rule, so the predicate lives here once.
// Returns rows by reference (no clone) — callers that mutate should clone
// the result themselves.
// ============================================================================

/**
 * @param {object|null|undefined} contract
 * @returns {Array} `contract.otherFees` if non-empty; else a synthesized
 *   `[{code:'treatment',kind:'flat',flatUsd}]` when
 *   `standardFees.treatmentFeeUsd` is a positive finite number; else `[]`.
 */
export const resolveOtherFeesWithLegacy = (contract) => {
  const rows = Array.isArray(contract?.otherFees) ? contract.otherFees : []
  if (rows.length) return rows
  const legacy = contract?.standardFees?.treatmentFeeUsd
  if (Number.isFinite(legacy) && legacy > 0) {
    return [{code: 'treatment', kind: 'flat', flatUsd: legacy}]
  }
  return []
}

// ============================================================================
// Assay-fee map (per-paid-metal `feeUsd` + `optInAtSalesOrder` opt-in tracking)
// ============================================================================

export const fanoutAssayFeeFromFlat = (assayFeeUsd, paidMetals) => {
  const metals = Array.isArray(paidMetals) ? paidMetals : []
  if (!metals.length) return {}
  const perMetalUsd = Number.isFinite(assayFeeUsd) ? assayFeeUsd / metals.length : 0
  const map = {}
  for (const metal of metals) {
    map[String(metal).toLowerCase()] = {feeUsd: perMetalUsd}
  }
  return map
}

/** Keep only paid metals; default missing entries to `{ feeUsd: 0 }`. */
export const sanitizeAssayFeeMap = (assayFee, paidMetals) => {
  const paid = (Array.isArray(paidMetals) ? paidMetals : []).map((metal) => String(metal).toLowerCase())
  const source = (assayFee && typeof assayFee === 'object') ? assayFee : {}
  const out = {}
  for (const metal of paid) {
    const row = source[metal] || {}
    const feeUsd = Number.isFinite(row.feeUsd) ? row.feeUsd : 0
    const entry = {feeUsd}
    if (row.optInAtSalesOrder === true) entry.optInAtSalesOrder = true
    if (typeof row.tierQualifyingMetal === 'string') entry.tierQualifyingMetal = row.tierQualifyingMetal
    if (Array.isArray(row.purityTiers)) entry.purityTiers = row.purityTiers.map((tier) => ({...tier}))
    out[metal] = entry
  }
  return out
}

/** Sync assay map when paidMetals changes (add defaults, strip removed). */
export const syncAssayFeeForPaidMetals = (assayFee, paidMetals) => {
  const paid = (Array.isArray(paidMetals) ? paidMetals : []).map((metal) => String(metal).toLowerCase())
  const source = (assayFee && typeof assayFee === 'object') ? assayFee : {}
  const out = {}
  for (const metal of paid) {
    out[metal] = source[metal] ? {...source[metal]} : {feeUsd: 0}
    if (!Number.isFinite(out[metal].feeUsd)) out[metal].feeUsd = 0
    if (out[metal].optInAtSalesOrder !== true) delete out[metal].optInAtSalesOrder
  }
  return out
}

export const resolveAssayFeeFromContract = (contract) => {
  if (contract?.assayFee && typeof contract.assayFee === 'object') {
    return sanitizeAssayFeeMap(contract.assayFee, contract.paidMetals)
  }
  const legacyUsd = contract?.standardFees?.assayFeeUsd
  if (Number.isFinite(legacyUsd)) {
    return fanoutAssayFeeFromFlat(legacyUsd, contract?.paidMetals)
  }
  return sanitizeAssayFeeMap({}, contract?.paidMetals)
}

export const resolveAssayFeeUsd = (contract, metal, qualifiers = {}) => {
  const key = String(metal).toLowerCase()
  const row = resolveAssayFeeFromContract(contract)[key]
  if (!row) return 0
  if (!Array.isArray(row.purityTiers) || row.purityTiers.length === 0) {
    return Number.isFinite(row.feeUsd) ? row.feeUsd : 0
  }
  const qualifier = row.tierQualifyingMetal || key
  const purity = qualifiers.purityPctByMetal?.[qualifier]
  const band = matchPurityBand(row.purityTiers, purity)
  return band && Number.isFinite(band.feeUsd) ? band.feeUsd : row.feeUsd
}

export const metalRequiresAssayOptIn = (contract, metal) => {
  const key = String(metal).toLowerCase()
  return Boolean(resolveAssayFeeFromContract(contract)[key]?.optInAtSalesOrder)
}

export const buildAssayOptInMapFromSettlement = (contract, assayOptIn = {}, contractFees = {}) => {
  const map = {}
  const settledAssay = contractFees?.perMetalAssayFee || {}
  for (const metal of (contract?.paidMetals || [])) {
    const key = String(metal).toLowerCase()
    if (!metalRequiresAssayOptIn(contract, key)) continue
    const row = settledAssay[key]
    map[key] = typeof row?.optedIn === 'boolean' ? row.optedIn : assayOptIn[key] === true
  }
  return map
}

export const sumIncludedAssayFeesUsd = (perMetalAssayFee = {}) => {
  let total = 0
  for (const row of Object.values(perMetalAssayFee)) {
    if (!row || row.optedIn === false) continue
    total += Number.isFinite(row.feeUsd) ? row.feeUsd : 0
  }
  return total
}

// ============================================================================
// Reference-only fee schedule shape validation
// ----------------------------------------------------------------------------
// REFERENCE-ONLY documentation — never consumed by computeSettlementPreview /
// computeSettlementTotals / resolveFeeAmount. Operators reconcile partner
// statements against it at transcription; anything billed lands in adHocFees[].
// Element coverage: Saxonia 11/03/26-065/54-1 (cd, ni, sn, zn, bi, pb, co,
// cr, sb, halides) + Asahi-silver SR2605539 (sb, bi, cu, hg, te, cd, sn, as,
// pb, se, be). Metalor-archive elements deferred.
// refiningCosts is a reference-only per-metal currency/kg-credited table;
// compute-side refining lives on metal_rates[].fee.
// ============================================================================

export const referenceFeeScheduleHasContent = (schedule) => {
  if (!isPlainObject(schedule)) return false
  return Object.values(schedule).some(hasMeaningfulValue)
}

/**
 * Strip sub-fields that are null / empty arrays / empty objects / whitespace
 * strings. An object that collapses entirely to empties returns `null`, so the
 * persisted shape has a single canonical "no schedule" sentinel.
 */
export const normalizeReferenceFeeSchedule = (input) => {
  if (!isPlainObject(input)) return null
  const out = {}
  Object.entries(input).forEach(([key, value]) => {
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed.length > 0) out[key] = trimmed
      return
    }
    if (hasMeaningfulValue(value)) out[key] = value
  })
  return Object.keys(out).length > 0 ? out : null
}

const pickFiniteThreshold = (band) => {
  if (Number.isFinite(band.maxPpm)) return band.maxPpm
  if (Number.isFinite(band.maxPct)) return band.maxPct
  return null
}

export const validateImpurityChargesShape = (impurityCharges) => {
  if (impurityCharges === null || impurityCharges === undefined) return []
  if (!Array.isArray(impurityCharges)) {
    return ['impurityCharges must be an array of element rows']
  }
  const errors = []
  impurityCharges.forEach((row, idx) => {
    const ordinal = idx + 1
    const prefix = `impurityCharges[${ordinal}]`
    if (!isPlainObject(row)) {
      errors.push(`${prefix} must be an object`)
      return
    }
    if (!REFERENCE_FEE_SCHEDULE_ELEMENTS.includes(row.element)) {
      errors.push(`${prefix} element must be one of: ${REFERENCE_FEE_SCHEDULE_ELEMENTS.join(', ')}`)
    }
    if (!IMPURITY_CHARGE_BASES.includes(row.basis)) {
      errors.push(`${prefix} basis must be one of: ${IMPURITY_CHARGE_BASES.join(', ')}`)
    }
    if (!Array.isArray(row.bands) || row.bands.length === 0) {
      errors.push(`${prefix} bands must contain at least one entry`)
      return
    }
    let lastThreshold = -Infinity
    let trailingUnboundedSeen = false
    row.bands.forEach((band, bandIdx) => {
      const bandOrdinal = bandIdx + 1
      const bandPrefix = `${prefix} band[${bandOrdinal}]`
      if (!isPlainObject(band)) {
        errors.push(`${bandPrefix} must be an object`)
        return
      }
      if (!ALLOWED_IMPURITY_BAND_STATES.includes(band.state)) {
        errors.push(`${bandPrefix} state must be one of: ${ALLOWED_IMPURITY_BAND_STATES.join(', ')}`)
      }
      if (band.state === IMPURITY_BAND_STATES.CHARGE && !Number.isFinite(band.ratePerKg)) {
        errors.push(`${bandPrefix} state === '${IMPURITY_BAND_STATES.CHARGE}' requires ratePerKg (finite number)`)
      }
      const threshold = pickFiniteThreshold(band)
      if (trailingUnboundedSeen) {
        errors.push(`${bandPrefix} no bands may follow the "and-above" unbounded band`)
      }
      if (threshold === null) {
        trailingUnboundedSeen = true
        return
      }
      if (threshold <= lastThreshold) {
        errors.push(`${prefix} bands must be monotonically increasing in maxPpm/maxPct`)
      }
      lastThreshold = threshold
    })
  })
  return errors
}

export const validateRefiningCostsShape = (refiningCosts, paidMetals, currencyEnum) => {
  if (refiningCosts === null || refiningCosts === undefined) return []
  if (!isPlainObject(refiningCosts)) return ['refiningCosts must be an object keyed by metal']
  const errors = []
  Object.entries(refiningCosts).forEach(([metal, row]) => {
    const prefix = `refiningCosts.${metal}`
    if (!Array.isArray(paidMetals) || !paidMetals.includes(metal)) {
      errors.push(`refiningCosts metal '${metal}' must be in paidMetals`)
    }
    if (!isPlainObject(row)) {
      errors.push(`${prefix} must be an object`)
      return
    }
    if (!Number.isFinite(row.ratePerKg) || row.ratePerKg <= 0) {
      errors.push(`${prefix} ratePerKg must be > 0`)
    }
    if (!REFINING_COST_BASES.includes(row.basis)) {
      errors.push(`${prefix} basis must be 'perKgCreditedMetal' (the only basis supported in this plan)`)
    }
    if (!Array.isArray(currencyEnum) || !currencyEnum.includes(row.currency)) {
      errors.push(`${prefix} currency must be one of: ${(currencyEnum || []).join(', ')}`)
    }
  })
  return errors
}

export const validateMetalThresholdsShape = (metalThresholds, paidMetals) => {
  if (metalThresholds === null || metalThresholds === undefined) return []
  if (!isPlainObject(metalThresholds)) return ['metalThresholds must be an object keyed by metal']
  const errors = []
  Object.entries(metalThresholds).forEach(([metal, row]) => {
    const prefix = `metalThresholds.${metal}`
    if (!Array.isArray(paidMetals) || !paidMetals.includes(metal)) {
      errors.push(`metalThresholds metal '${metal}' must be in paidMetals`)
    }
    if (!isPlainObject(row)) {
      errors.push(`${prefix} must be an object`)
      return
    }
    ['minimumPayablePct', 'minimumDeductionPct'].forEach((key) => {
      if (row[key] === null || row[key] === undefined) return
      if (!Number.isFinite(row[key]) || row[key] < 0 || row[key] > 100) {
        errors.push(`${prefix}.${key} must be in [0, 100]`)
      }
    })
  })
  return errors
}
